import { useMemo, type ReactNode } from 'react';
import { useLoaderData } from 'react-router';

import { cn } from '@rocco/utils/cn';
import { Image } from '@rocco/components/image';
import { RoccoLink } from '@rocco/components/link';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import { useTilesAdminData } from '~/modules-rocco/admin/hooks/use-tiles-admin-data';
import { useRailTileClickReport } from '~/ui-rocco/hooks/tile-click-event-for-section';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import {
    CONTENT_CAP_PADDED,
    SECTION_TITLE_TYPE,
} from '~/ui-rocco/pages/detail-shared';
import { CategoryPageTileWithAdminActions } from '~/ui-rocco/pages/category-page/CategoryPageTileWithAdminActions';
import { DARK_TILE_CAPTIONS, GRID_CLASS } from './ProjectUsedSections';

const NUMBER_WORDS = [
    'No',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
    'Ten',
    'Eleven',
    'Twelve',
    'Thirteen',
    'Fourteen',
    'Fifteen',
    'Sixteen',
    'Seventeen',
    'Eighteen',
    'Nineteen',
    'Twenty',
];

/** "Thirteen products", or "24 products" past what reads as a word. */
const countWord = (count: number) => NUMBER_WORDS[count] ?? String(count);

/** One entry of the sourcebook: the product, and who supplies it. */
const SourcebookRow = ({ product }: { product: ProductTileViewModel }) => {
    const report = useRailTileClickReport('ProjectUsedProducts', 'ProductTile');
    const onClick = () =>
        report({
            id: product.id,
            title: product.title,
            professionalId: product.professional?.id,
        });
    return (
        <li className="flex break-inside-avoid items-center gap-[1.5rem] border-b border-white/15 py-[1rem]">
            <RoccoLink
                to={product.link}
                onClick={onClick}
                aria-label={product.title}
                className="relative block size-[4rem] shrink-0 overflow-hidden rounded-[0.25rem] bg-white/5"
            >
                {product.images[0] ? (
                    <Image
                        src={product.images[0].src}
                        alt=""
                        fill
                        fit="cover"
                        sizes="64px"
                        className="absolute inset-0"
                    />
                ) : null}
            </RoccoLink>
            <div className="min-w-0 flex-1">
                <RoccoLink
                    to={product.link}
                    onClick={onClick}
                    className="block font-sans text-[1.125rem] leading-[1.3] font-medium text-white no-underline transition-colors hover:text-white/70"
                >
                    {product.title}
                </RoccoLink>
                <RoccoLink
                    to={product.professional.link}
                    className="mt-[0.25rem] block font-sans text-[0.9375rem] leading-[1.3] text-[#a3a3a3] no-underline transition-colors hover:text-white"
                >
                    {product.professional.name}
                </RoccoLink>
            </div>
            <RoccoLink
                to={product.link}
                onClick={onClick}
                aria-label={`View ${product.title}`}
                className="shrink-0 font-sans text-[0.75rem] leading-none tracking-[0.14em] text-[#a3a3a3] uppercase no-underline transition-colors hover:text-white"
            >
                View
            </RoccoLink>
        </li>
    );
};

/** Past this many products the sourcebook runs as an index; at or under it, as tiles. */
const TILE_LIMIT = 3;

/**
 * A single product, as one landscape picture filling the three columns the
 * tiles would take, with the tile's own caption under it.
 */
const SourcebookFeature = ({ product }: { product: ProductTileViewModel }) => {
    const report = useRailTileClickReport('ProjectUsedProducts', 'ProductTile');
    const onClick = () =>
        report({
            id: product.id,
            title: product.title,
            professionalId: product.professional?.id,
        });
    return (
        <div className="md:col-span-3">
            <RoccoLink
                to={product.link}
                onClick={onClick}
                aria-label={product.title}
                className="relative block aspect-[16/10] w-full overflow-hidden rounded-tile bg-white/5"
            >
                {product.images[0] ? (
                    <Image
                        src={product.images[0].src}
                        alt=""
                        fill
                        fit="cover"
                        sizes="(min-width: 768px) 60vw, 100vw"
                        className="absolute inset-0 transition-transform duration-500 ease-out hover:scale-[1.03] motion-reduce:transition-none"
                    />
                ) : null}
            </RoccoLink>
            <div className="mt-[1rem] flex flex-col gap-[0.25rem]">
                <RoccoLink
                    to={product.link}
                    onClick={onClick}
                    className="font-sans text-[1.125rem] leading-[1.3] font-medium text-offwhite no-underline transition-colors hover:text-white/70"
                >
                    {product.title}
                </RoccoLink>
                <RoccoLink
                    to={product.professional.link}
                    className="font-sans text-[0.9375rem] leading-[1.3] text-white/60 no-underline transition-colors hover:text-white"
                >
                    {product.professional.name}
                </RoccoLink>
            </div>
        </div>
    );
};

/**
 * Up to three products, on the page's five-column product grid: the text
 * takes the first two columns and the tiles range right from the last, so
 * two products sit in the fourth and fifth columns and three fill the
 * remaining three. One product becomes a landscape feature across them.
 */
const SourcebookTiles = ({
    products,
    intro,
}: {
    products: TileViewModel<ProductTileViewModel>[];
    intro: ReactNode;
}) => (
    <div className={cn(GRID_CLASS, DARK_TILE_CAPTIONS, 'md:items-start')}>
        <div className="max-md:col-span-2 md:col-span-2">{intro}</div>
        {products.length === 1 ? (
            <SourcebookFeature product={products[0]!.model} />
        ) : (
            products.map((tile, index) => (
                <div
                    key={tile.model.id}
                    // The first tile starts at the column that leaves the
                    // last one on the page's right edge.
                    className={cn(
                        index === 0 &&
                            products.length === 2 &&
                            'md:col-start-4',
                    )}
                >
                    <CategoryPageTileWithAdminActions tile={tile} />
                </div>
            ))
        )}
    </div>
);

/**
 * The products behind the project, as a sourcebook: the
 * statement in Gestura capitals and a line about what was specified on the
 * left, and on the right every tagged product as a row — thumbnail, name,
 * supplier, View — flowing down two columns. The project page's own take on
 * Products used; the other detail pages keep their tile grid.
 */
export const ProjectSourcebookSection = () => {
    const { featuredProducts } = useLoaderData<typeof loader>();
    const products = featuredProducts?.FeaturedProducts?.products;
    const rows = useMemo(() => products ?? [], [products]);
    // The short list's tiles, built whatever the count so the hooks below
    // run in the same order on every render.
    const tiles: TileViewModel<ProductTileViewModel>[] = useMemo(
        () =>
            rows.map((product) => ({
                model: product,
                aspectRatio: 'productTall' as const,
                // The name the legacy rail used. `ProjectSourcebook` is this
                // section's own word for itself and matches no case, so it
                // reported the generic tile click for a rail that had a name.
                section: 'ProjectUsedProducts',
            })),
        [rows],
    );
    const tilesWithAdmin = useTilesAdminData(tiles);
    if (rows.length === 0) return null;

    const suppliers = new Set(rows.map((product) => product.professional.name));
    const summary = `${countWord(rows.length)} ${rows.length === 1 ? 'product' : 'products'} specified into this build from ${suppliers.size === 1 ? 'one supplier, a verified ArchiPro member' : `${countWord(suppliers.size).toLowerCase()} suppliers, verified ArchiPro members`}. Tap through to specifications, suppliers and lead times.`;

    const intro = (
        <div>
            {/* The display line the heads below it are set in — Professionals
                used, More projects from — so the tail reads as one voice. */}
            <h2
                className={cn('m-0 max-w-[9em] text-white', SECTION_TITLE_TYPE)}
            >
                The products behind the project
            </h2>
            {/* Desktop only: the phone reads the head alone, as the product
                page's closing sections do. */}
            <p className="m-0 mt-[2rem] max-w-[28rem] font-sans text-[1.0625rem] leading-[1.6] text-white/70 max-md:hidden">
                {summary}
            </p>
        </div>
    );

    // A handful of products are shown, not indexed: as tiles on the page's
    // product grid, ranged right of the statement.
    if (rows.length <= TILE_LIMIT) {
        return (
            <section className="w-full bg-transparent text-white max-md:px-6 md:px-[3rem]">
                <div className={CONTENT_CAP_PADDED}>
                    <SourcebookTiles products={tilesWithAdmin} intro={intro} />
                </div>
            </section>
        );
    }

    return (
        <section className="w-full bg-transparent text-white max-md:px-6 md:px-[3rem]">
            <div
                className={cn(
                    CONTENT_CAP_PADDED,
                    'grid max-md:gap-10 md:grid-cols-[minmax(0,1fr)_minmax(0,2.3fr)] md:gap-[8rem]',
                )}
            >
                {/* The cell stays stretched to the row's height and the
                    statement sticks inside it — a cell sized to its own
                    content gives a sticky child no room to travel, so it
                    would never move. It holds 48px off the top, the page's
                    own gutter, and stops when the index beside it runs out.
                    Twenty-six products run several screens deep, and the
                    statement saying what the list is was long gone by the
                    time the reader was halfway down it. */}
                <div>
                    <div className="md:sticky md:top-[3rem]">{intro}</div>
                </div>
                {/* Column-first, so the list fills the left column before it
                    starts the right, as a printed index does. */}
                <ul className="m-0 list-none border-t border-white/15 p-0 md:columns-2 md:gap-[3.75rem]">
                    {rows.map((product) => (
                        <SourcebookRow key={product.id} product={product} />
                    ))}
                </ul>
            </div>
        </section>
    );
};
